import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calendar, Plus, Trash2, X, ChevronRight, Loader2, 
  FileText, Download, Sparkles, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const THEMES = [
  'Faith', 'Love', 'Hope', 'Forgiveness', 'Prayer', 'Worship',
  'Discipleship', 'Evangelism', 'Family', 'Stewardship', 
  'Spiritual Growth', 'Community', 'Healing', 'Justice'
];

export default function SermonPlanner({ onClose, onGenerateStudy }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [generating, setGenerating] = useState(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    plan_name: '',
    duration: 'quarter',
    start_date: new Date().toISOString().split('T')[0],
    themes: [],
    audience: 'General Congregation',
    language: 'en'
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await axios.get(`${API}/planner/list`);
      setPlans(response.data);
    } catch (err) {
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const loadPlan = async (planId) => {
    try {
      const response = await axios.get(`${API}/planner/${planId}`);
      setSelectedPlan(response.data);
    } catch (err) {
      setError('Failed to load plan details');
    }
  };

  const createPlan = async () => {
    if (!formData.plan_name.trim()) {
      setError('Please enter a plan name');
      return;
    }
    
    setCreating(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/planner/create`, formData, { timeout: 30000 });
      
      if (response.data.job_id) {
        const jobId = response.data.job_id;
        let attempts = 0;
        while (attempts < 60) {
          await new Promise(r => setTimeout(r, 5000));
          attempts++;
          try {
            const status = await axios.get(`${API}/generate/status/${jobId}`, { timeout: 10000 });
            if (status.data.status === 'completed') {
              setSelectedPlan({ ...formData, id: status.data.id, plan_data: status.data.plan });
              setShowCreateForm(false);
              loadPlans();
              return;
            } else if (status.data.status === 'failed') {
              setError(status.data.error || 'Failed to create plan');
              return;
            }
          } catch { /* continue polling */ }
        }
        setError('Plan generation timed out. Please try again.');
      } else {
        setSelectedPlan({ ...formData, id: response.data.id, plan_data: response.data.plan });
        setShowCreateForm(false);
        loadPlans();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create plan');
    } finally {
      setCreating(false);
    }
  };

  const deletePlan = async (planId) => {
    if (!window.confirm('Delete this sermon plan?')) return;
    
    try {
      await axios.delete(`${API}/planner/${planId}`);
      setPlans(plans.filter(p => p.id !== planId));
      if (selectedPlan?.id === planId) {
        setSelectedPlan(null);
      }
    } catch (err) {
      setError('Failed to delete plan');
    }
  };

  const generateStudyFromWeek = async (weekNumber) => {
    if (!selectedPlan) return;
    
    setGenerating(weekNumber);
    setError('');
    
    try {
      const response = await axios.post(
        `${API}/planner/${selectedPlan.id}/generate/${weekNumber}`,
        {},
        { timeout: 30000 }
      );
      
      if (response.data.job_id) {
        const jobId = response.data.job_id;
        let attempts = 0;
        while (attempts < 60) {
          await new Promise(r => setTimeout(r, 5000));
          attempts++;
          try {
            const status = await axios.get(`${API}/generate/status/${jobId}`, { timeout: 10000 });
            if (status.data.status === 'completed') {
              if (onGenerateStudy) {
                onGenerateStudy(status.data.content, status.data.week_data);
              }
              onClose();
              return;
            } else if (status.data.status === 'failed') {
              setError(status.data.error || 'Failed to generate study');
              return;
            }
          } catch { /* continue polling */ }
        }
        setError('Generation timed out. Please try again.');
      } else {
        if (onGenerateStudy) {
          onGenerateStudy(response.data.content, response.data.week_data);
        }
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate study');
    } finally {
      setGenerating(null);
    }
  };

  const toggleTheme = (theme) => {
    setFormData(prev => ({
      ...prev,
      themes: prev.themes.includes(theme)
        ? prev.themes.filter(t => t !== theme)
        : [...prev.themes, theme]
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="sermon-planner-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-[#E2DFD8]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-2xl font-bold text-[#1B3A6B]">Sermon Series Planner</h2>
              <p className="text-[#475569] mt-1">Plan your sermons for a quarter, half-year, or full year</p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg">
              <X className="w-5 h-5 text-[#475569]" />
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left Panel - Plans List */}
          <div className="w-72 border-r border-[#E2DFD8] flex flex-col">
            <div className="p-4">
              <button
                onClick={() => setShowCreateForm(true)}
                className="w-full btn-primary py-3 rounded-lg font-medium flex items-center justify-center gap-2"
                data-testid="create-plan-button"
              >
                <Plus className="w-5 h-5" />
                New Plan
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 pt-0 space-y-2">
              {loading ? (
                <div className="text-center py-8 text-[#475569]">Loading...</div>
              ) : plans.length === 0 ? (
                <div className="text-center py-8 text-[#475569]">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No plans yet</p>
                </div>
              ) : (
                plans.map(plan => (
                  <button
                    key={plan.id}
                    onClick={() => loadPlan(plan.id)}
                    className={`w-full text-left p-4 rounded-lg transition-colors ${
                      selectedPlan?.id === plan.id
                        ? 'bg-[#1B3A6B] text-white'
                        : 'bg-[#F9F8F6] hover:bg-[#F4F2EC]'
                    }`}
                    data-testid={`plan-${plan.id}`}
                  >
                    <h4 className="font-medium">{plan.plan_name}</h4>
                    <p className={`text-sm mt-1 ${selectedPlan?.id === plan.id ? 'text-white/70' : 'text-[#475569]'}`}>
                      {plan.duration} · Starting {new Date(plan.start_date).toLocaleDateString()}
                    </p>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Right Panel - Plan Details or Create Form */}
          <div className="flex-1 overflow-y-auto p-6">
            {showCreateForm ? (
              <div className="max-w-xl mx-auto">
                <h3 className="font-heading text-xl font-semibold text-[#1B3A6B] mb-6">Create New Sermon Plan</h3>
                
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Plan Name *</label>
                    <input
                      type="text"
                      value={formData.plan_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, plan_name: e.target.value }))}
                      className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]"
                      placeholder="e.g., 2026 Q1 Sermon Series"
                      data-testid="plan-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Duration</label>
                      <select
                        value={formData.duration}
                        onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                        className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]"
                        data-testid="plan-duration-select"
                      >
                        <option value="quarter">Quarter (13 weeks)</option>
                        <option value="half-year">Half Year (26 weeks)</option>
                        <option value="year">Full Year (52 weeks)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0F1A2A] mb-1.5">Start Date</label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                        className="w-full px-4 py-3 bg-[#F9F8F6] rounded-lg border-none focus:ring-2 focus:ring-[#1B3A6B]"
                        data-testid="plan-start-date"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F1A2A] mb-2">Themes to Include</label>
                    <div className="flex flex-wrap gap-2">
                      {THEMES.map(theme => (
                        <button
                          key={theme}
                          onClick={() => toggleTheme(theme)}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            formData.themes.includes(theme)
                              ? 'bg-[#1B3A6B] text-white'
                              : 'bg-[#F4F2EC] text-[#475569] hover:bg-[#E2DFD8]'
                          }`}
                        >
                          {theme}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setShowCreateForm(false)}
                      className="flex-1 btn-secondary py-3 rounded-lg font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={createPlan}
                      disabled={creating}
                      className="flex-1 btn-primary py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="generate-plan-button"
                    >
                      {creating ? (
                        <><Loader2 className="w-5 h-5 animate-spin" />Generating Plan...</>
                      ) : (
                        <><Sparkles className="w-5 h-5" />Generate Plan</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ) : selectedPlan ? (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-[#1B3A6B]">{selectedPlan.plan_name}</h3>
                    <p className="text-[#475569] mt-1">
                      {selectedPlan.duration} · Starting {new Date(selectedPlan.start_date).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => deletePlan(selectedPlan.id)}
                    className="p-2 text-[#475569] hover:text-red-500 rounded-lg"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                {selectedPlan.plan_data?.overview && (
                  <div className="mb-6 p-4 bg-[#FFF8E7] border border-[#B8860B]/30 rounded-xl">
                    <p className="text-[#0F1A2A]">{selectedPlan.plan_data.overview}</p>
                  </div>
                )}

                <div className="space-y-3">
                  {selectedPlan.plan_data?.weeks?.map((week, index) => (
                    <div
                      key={index}
                      className="p-4 bg-[#F9F8F6] rounded-xl hover:bg-[#F4F2EC] transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="w-8 h-8 bg-[#1B3A6B] text-white rounded-full flex items-center justify-center text-sm font-medium">
                              {week.week_number}
                            </span>
                            <div>
                              <h4 className="font-semibold text-[#0F1A2A]">{week.sermon_title}</h4>
                              <p className="text-sm text-[#475569]">
                                {week.date ? new Date(week.date).toLocaleDateString() : ''} · {week.theme}
                              </p>
                            </div>
                          </div>
                          <p className="text-sm text-[#1B3A6B] ml-11">{week.scriptures}</p>
                          {week.key_points && (
                            <ul className="text-sm text-[#475569] ml-11 mt-2 list-disc list-inside">
                              {week.key_points.map((point, i) => (
                                <li key={i}>{point}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <button
                          onClick={() => generateStudyFromWeek(week.week_number)}
                          disabled={generating === week.week_number}
                          className="btn-accent px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-50"
                          data-testid={`generate-week-${week.week_number}`}
                        >
                          {generating === week.week_number ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4" />
                          )}
                          Generate
                        </button>
                      </div>
                    </div>
                  )) || (
                    <div className="text-center py-8 text-[#475569]">
                      <p>Plan data is loading or unavailable</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center">
                <Calendar className="w-16 h-16 text-[#475569] opacity-30 mb-4" />
                <h3 className="font-heading text-xl font-semibold text-[#1B3A6B] mb-2">Plan Your Sermons</h3>
                <p className="text-[#475569] max-w-md mb-6">
                  Create a comprehensive sermon plan for the quarter or year. AI will suggest scriptures, themes, and timing based on the church calendar.
                </p>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="btn-primary px-6 py-3 rounded-lg font-medium flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create Your First Plan
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
